import { useTranslation } from 'react-i18next';
import { EyeOff } from 'lucide-react';
import { formatPriceMinor, noOptionsReasonShort } from '@/routes/disruptions/labels';
import { formatInstant } from '@/lib/why/format';
import type { WhyEvidence, WhyEvidenceSection } from '@/lib/why/types';

/**
 * The LIVE half of the dialog: `resolution-state@v1` counters composed at READ
 * time, so a chapter written an hour ago sits above numbers that report NOW.
 *
 * ONE RULE GOVERNS THIS FILE. A section the BFF could not read arrives
 * `known:false` with EMPTY counters, and it renders as "we could not read this"
 * — never as zeros. Zeros dressed as facts are the single failure mode the
 * whole feature exists to prevent (brief §6.2), and defaulting `?? 0` anywhere
 * below would reintroduce it. Absent is not zero.
 */

/** Minor-unit money counters — formatted with the section's own currency. */
const MINOR_SUFFIX = /Minor$/;

export function EvidencePanel({ evidence }: { evidence: WhyEvidence }) {
  const { t } = useTranslation('why');

  if (!evidence || !evidence.known) {
    return (
      <section data-testid="why-evidence" data-known="false">
        <EvidenceHeading />
        <p
          className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
          data-testid="why-evidence-unknown"
        >
          <EyeOff className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {t('evidence.unknownAll')}
        </p>
      </section>
    );
  }

  return (
    <section data-testid="why-evidence" data-known="true">
      <EvidenceHeading computedAt={evidence.computedAt} />
      <div className="grid gap-2 sm:grid-cols-2">
        {evidence.sections.map((section) => (
          <EvidenceSectionCard key={section.id} section={section} />
        ))}
      </div>
    </section>
  );
}

function EvidenceHeading({ computedAt }: { computedAt?: string | null }) {
  const { t } = useTranslation('why');
  return (
    <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('evidence.heading')}
      </h3>
      {computedAt ? (
        <p className="text-[10.5px] text-muted-foreground" data-testid="why-evidence-computed-at">
          {t('evidence.computedAt', { at: formatInstant(computedAt) })}
        </p>
      ) : null}
    </div>
  );
}

function EvidenceSectionCard({ section }: { section: WhyEvidenceSection }) {
  const { t } = useTranslation('why');
  const counters = Object.entries(section.counters ?? {});
  const reasons = Object.entries(section.reasons ?? {}).filter(([, n]) => n > 0);

  return (
    <div
      className="rounded-lg border border-border bg-background p-2.5"
      data-testid={`why-evidence-section-${section.id}`}
      data-known={section.known ? 'true' : 'false'}
    >
      <h4 className="mb-1 text-xs font-semibold text-foreground">
        {t(`evidence.section.${section.id}`)}
      </h4>

      {!section.known ? (
        <p
          className="flex items-start gap-1.5 text-[11.5px] text-amber-700 dark:text-amber-300"
          data-testid={`why-evidence-section-${section.id}-unknown`}
        >
          <EyeOff className="mt-px h-3 w-3 shrink-0" aria-hidden />
          {/* The price section that could not be priced says «No disponible», not "unread". */}
          {section.id === 'price' && section.priced === false
            ? t('evidence.priceUnavailable')
            : t('evidence.unknown')}
        </p>
      ) : (
        <>
          <dl className="grid gap-y-0.5 text-[11.5px]">
            {counters.map(([key, value]) => (
              <div key={key} className="flex items-baseline justify-between gap-3">
                <dt className="min-w-0 truncate text-muted-foreground">
                  {t(`evidence.counter.${key}`, { defaultValue: key })}
                </dt>
                <dd className="shrink-0 font-semibold tabular-nums text-foreground">
                  {/* airline-money@v2 — a `*Minor` counter is the airline's PRICE. */}
                  {MINOR_SUFFIX.test(key) && section.currency
                    ? formatPriceMinor(value, section.currency, t)
                    : value.toLocaleString()}
                </dd>
              </div>
            ))}
          </dl>

          {section.priceSource ? (
            <p className="mt-1 text-[10.5px] text-muted-foreground">
              {t(`evidence.priceSource.${section.priceSource}`)}
            </p>
          ) : null}

          {reasons.length ? (
            <div className="mt-1.5" data-testid={`why-evidence-section-${section.id}-reasons`}>
              <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('evidence.reasons')}
              </p>
              <ul className="mt-0.5 flex flex-wrap gap-1">
                {reasons.map(([code, n]) => (
                  <li
                    key={code}
                    className="rounded-full bg-muted px-1.5 py-px text-[10.5px] text-foreground"
                    data-testid={`why-evidence-reason-${code}`}
                  >
                    {noOptionsReasonShort(code) ?? code}
                    <span className="ml-1 font-semibold tabular-nums">{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
